/**
 * Per-session slide tracking for the investor deck (A7Y-4448).
 *
 * Mounted by both deck layouts — `DeckRunner` (one slide at a time) and
 * `StoryPage` (the mobile scroll, whose current slide the intersection
 * observer reports) — and by nothing else: the print view is not a reading.
 * Every visit goes out as a `sendBeacon` to the admin worker, which is the
 * only transport that survives a tab close; nothing here can affect the page.
 */
import { useEffect, useRef } from 'react';
import { DECK_VIEW_BEACON_PATH, type DeckViewBeacon, type DeckViewMode } from './deck-view-beacon';
import { DeckViewTracker } from './deck-view-tracker';
import type { Deck } from './types';

let sessionId: string | null = null;

/**
 * One id per page load, shared by every tracker the page mounts — a viewport
 * resize that swaps the desktop runner for the mobile story (or back) stays
 * one viewing session.
 */
export function deckViewSessionId(): string {
  if (sessionId) return sessionId;
  sessionId =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  return sessionId;
}

const monotonicNow = (): number => performance.now();

function sendDeckViewBeacon(beacon: DeckViewBeacon): void {
  const body = JSON.stringify(beacon);
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      if (
        navigator.sendBeacon(DECK_VIEW_BEACON_PATH, new Blob([body], { type: 'application/json' }))
      ) {
        return;
      }
    }
    void fetch(DECK_VIEW_BEACON_PATH, {
      method: 'POST',
      body,
      headers: { 'content-type': 'application/json' },
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    // Tracking never surfaces to the viewer.
  }
}

export function useDeckViewTracking(deck: Deck, index: number, mode: DeckViewMode): void {
  const trackerRef = useRef<DeckViewTracker | null>(null);

  // Declared first so the tracker exists before the slide effect below runs
  // on mount (effects run in declaration order).
  useEffect(() => {
    const tracker = new DeckViewTracker({
      session: deckViewSessionId(),
      deck: deck.slug,
      slides: deck.slides.length,
      mode,
      send: sendDeckViewBeacon,
      now: monotonicNow,
    });
    trackerRef.current = tracker;
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') tracker.hide();
      else tracker.show();
    };
    const onPageHide = () => tracker.leave();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
      tracker.leave();
      trackerRef.current = null;
    };
  }, [deck, mode]);

  useEffect(() => {
    const slide = deck.slides[index];
    if (slide) trackerRef.current?.enter(index, slide.id);
  }, [deck, index]);
}
